import { OneStemBrand } from '@/components/brand/OneStemBrand'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAppForm } from '@/lib/form'
import { useResetPasswordMutation } from '@/lib/services/auth/auth.query'
import { resetPasswordSchema } from '@/lib/services/auth/auth.schema'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'

export const Route = createFileRoute('/(auth)/reset-password/$token')({
  component: RouteComponent,
  loader: () => ({ isReadOnly: false }),
  // Don't match this route unless the search params are valid
  validateSearch: resetPasswordSchema.pick({ email: true }),
  params: {
    // Don't match this route unless the route (path) params are valid
    parse: (params) => resetPasswordSchema.pick({ token: true }).parse(params),
  },
})

function RouteComponent() {
  const { token } = Route.useParams()
  const { email } = Route.useSearch()
  const navigate = useNavigate()

  const resetPasswordMutation = useResetPasswordMutation()

  const form = useAppForm({
    defaultValues: {
      email,
      token,
      password: '',
      password_confirmation: '',
    },
    validators: {
      onSubmit: resetPasswordSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await resetPasswordMutation.mutateAsync(value, {
            onSuccess: () => {
              toast.success('Password reset successful')
              void navigate({ to: '/login' })
            },
          })
        } catch (error) {
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <div className="flex flex-col gap-y-4 h-screen items-center justify-center px-8 sm:px-0">
      <Link to="/">
        <OneStemBrand className="h-20" />
      </Link>
      <Card className="w-full sm:max-w-md">
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <CardContent>
          <form.AppForm>
            <form.Form>
              <form.AppField name="token">
                {(field) => <field.TextInput type="hidden" />}
              </form.AppField>
              <form.AppField name="email">
                {(field) => <field.TextInput type="hidden" />}
              </form.AppField>
              <div className="grid w-full items-center gap-6">
                <form.FormError />
                <form.AppField
                  name="password"
                  validators={{ onChange: resetPasswordSchema.shape.password }}
                >
                  {(field) => <field.TextInput id="password" label="Password" type="password" />}
                </form.AppField>
                <form.AppField
                  name="password_confirmation"
                  validators={{
                    onChangeListenTo: ['password'],
                    onChange: ({ value, fieldApi }) => {
                      if (value !== fieldApi.form.getFieldValue('password'))
                        return 'Passwords do not match'
                    },
                  }}
                >
                  {(field) => (
                    <field.TextInput
                      id="password_confirmation"
                      label="Confirm Password"
                      type="password"
                    />
                  )}
                </form.AppField>
                <div>
                  <form.SubmitButton mutation={resetPasswordMutation}>
                    Reset password
                  </form.SubmitButton>
                </div>
              </div>
            </form.Form>
          </form.AppForm>
        </CardContent>
      </Card>
    </div>
  )
}
